// Render a laid-out panel sheet (from panelSheet.buildPanelSheet) as an
// AutoCAD R12-compatible ASCII DXF file. This format is the de-facto
// interchange for laser cutters, plotter cutters (Cricut / Silhouette),
// and CAD/CAM software.
//
// Layers used:
//   CUT   (black)   solid outer polygon — panel outline including seam allowance
//   SEAM  (magenta) dashed inner polygon — stitching line (also where holes sit)
//   HOLES (yellow)  circles for stitch holes
//   FOAM  (yellow)  solid inner polygon — foam-liner cut (regulation only)
//   LABEL (cyan)    edge-neighbour text tags
//   TITLE (black)   sheet legend
//
// DXF uses group-code / value pairs. Each pair is a line for the code, a
// line for the value. Numeric codes: 0=entity, 2=name, 8=layer, 10/20/30=x/y/z,
// 40=radius, 62=colour index, 70=flags, 1=text.
//
// The output is deterministic so file diffs stay tidy.

const CR = '\r\n';

function pair(code, value) {
  return `${code}${CR}${value}${CR}`;
}

function header(sheetWidthMM, sheetHeightMM) {
  // Minimal header — sets units to millimetres and the drawing extents so
  // consumers auto-zoom to fit.
  return (
    pair(0, 'SECTION') +
    pair(2, 'HEADER') +
    pair(9, '$INSUNITS') + pair(70, 4) +          // 4 = millimetres
    pair(9, '$MEASUREMENT') + pair(70, 1) +       // 1 = metric
    pair(9, '$EXTMIN') + pair(10, 0) + pair(20, 0) + pair(30, 0) +
    pair(9, '$EXTMAX') + pair(10, sheetWidthMM) + pair(20, sheetHeightMM) + pair(30, 0) +
    pair(0, 'ENDSEC')
  );
}

function tables() {
  const layer = (name, colour) =>
    pair(0, 'LAYER') +
    pair(2, name) +
    pair(70, 0) +          // flags
    pair(62, colour) +     // AutoCAD colour index
    pair(6, 'CONTINUOUS');

  // Strict DXF loaders (AutoCAD, LibreCAD, some CAM tools) require the
  // LTYPE table to be declared BEFORE the LAYER table, and each layer's
  // linetype reference (`6 CONTINUOUS`) must resolve to a linetype defined
  // in that section. Otherwise the file errors with
  // "LTYPE table is not found before LAYER".
  const ltype =
    pair(0, 'TABLE') + pair(2, 'LTYPE') + pair(70, 1) +
    pair(0, 'LTYPE') +
    pair(2, 'CONTINUOUS') +
    pair(70, 0) +
    pair(3, 'Solid line') +
    pair(72, 65) +         // alignment code — always 65 ('A')
    pair(73, 0) +          // 0 dash items
    pair(40, 0) +          // total pattern length 0
    pair(0, 'ENDTAB');

  // Strict DXF loaders (AutoCAD LT, CorelDRAW, LightBurn, some laser CAM
  // tools) also require a STYLE table before any TEXT/MTEXT entity is
  // used, otherwise the file errors with "STYLE table is not found". We
  // declare a single "STANDARD" style that every TEXT entity references
  // via group code 7.
  const style =
    pair(0, 'TABLE') + pair(2, 'STYLE') + pair(70, 1) +
    pair(0, 'STYLE') +
    pair(2, 'STANDARD') +
    pair(70, 0) +          // flags
    pair(40, 0) +          // fixed text height (0 = variable)
    pair(41, 1) +          // width factor
    pair(50, 0) +          // oblique angle
    pair(71, 0) +          // text generation flags
    pair(42, 2.5) +        // last text height used
    pair(3, 'txt') +       // primary font file
    pair(4, '') +          // bigfont file
    pair(0, 'ENDTAB');

  return (
    pair(0, 'SECTION') +
    pair(2, 'TABLES') +
    ltype +
    style +
    pair(0, 'TABLE') + pair(2, 'LAYER') + pair(70, 6) +
    layer('CUT', 7) +      // 7 = white/black (whichever contrasts)
    layer('SEAM', 1) +     // 1 = red (matches the red dashed SVG stitch line)
    layer('HOLES', 3) +    // 3 = green (matches the SVG green hole stroke)
    layer('FOAM', 2) +     // 2 = yellow (matches the yellow SVG foam line)
    layer('LABEL', 4) +    // 4 = cyan
    layer('TITLE', 7) +
    pair(0, 'ENDTAB') +
    pair(0, 'ENDSEC')
  );
}

function polyline(points, layer, closed) {
  // LWPOLYLINE is the compact 2D polyline supported by every DXF consumer.
  let out =
    pair(0, 'LWPOLYLINE') +
    pair(8, layer) +
    pair(90, points.length) +
    pair(70, closed ? 1 : 0);
  for (const [x, y] of points) {
    out += pair(10, x.toFixed(4)) + pair(20, y.toFixed(4));
  }
  return out;
}

function circle(x, y, r, layer) {
  return (
    pair(0, 'CIRCLE') +
    pair(8, layer) +
    pair(10, x.toFixed(4)) +
    pair(20, y.toFixed(4)) +
    pair(40, r.toFixed(4))
  );
}

function text(x, y, height, str, layer) {
  return (
    pair(0, 'TEXT') +
    pair(8, layer) +
    pair(7, 'STANDARD') +  // style name — must match the STYLE table entry
    pair(10, x.toFixed(4)) +
    pair(20, y.toFixed(4)) +
    pair(40, height.toFixed(3)) +
    pair(1, str) +
    pair(72, 1) +   // horizontal align: center
    pair(11, x.toFixed(4)) +
    pair(21, y.toFixed(4))
  );
}

function blocks() {
  // Strict DXF loaders (AutoCAD LT, LightBurn, some CAM tools) require the
  // BLOCKS section to exist between TABLES and ENTITIES, and it must
  // contain the two mandatory model-space / paper-space block records —
  // otherwise the file errors with "BLOCKS section is not exists".
  const emptyBlock = (name) =>
    pair(0, 'BLOCK') +
    pair(8, '0') +
    pair(2, name) +
    pair(70, 0) +
    pair(10, 0) + pair(20, 0) + pair(30, 0) +
    pair(3, name) +
    pair(1, '') +
    pair(0, 'ENDBLK') +
    pair(8, '0');
  return (
    pair(0, 'SECTION') +
    pair(2, 'BLOCKS') +
    emptyBlock('*MODEL_SPACE') +
    emptyBlock('*PAPER_SPACE') +
    pair(0, 'ENDSEC')
  );
}

/**
 * Render the laid-out panel sheet as a DXF string.
 * @param {object} laid       Output of layoutPanels (buildPanelSheet(...).laid).
 * @param {object} opts       Same commonOpts shape returned by buildPanelSheet.
 * @returns {string}          DXF file content, ready to save with mime type
 *                            application/dxf (or text/plain).
 */
export function renderDXF(laid, opts) {
  const {
    sheetWidthMM,
    marginMM,
    holeDiameterMM,
    ballDiameterMM,
    seamAllowanceMM,
    holeSpacingMM,
    foamInsetMM = 0,
    spherized,
    holeStyle = 'round',
    slitLengthMM = 1,
  } = opts;

  const holeRadius = holeDiameterMM / 2;
  const slitHalf = holeStyle === 'slit'
    ? (Number.isFinite(slitLengthMM) && slitLengthMM > 0 ? slitLengthMM : 1) / 2
    : 0;
  const parts = [];

  // Flip Y so downstream apps that treat DXF Y+ as "up" (most do) show the
  // sheet with the same orientation as the SVG preview.
  const flipY = (pt) => [pt[0], laid.sheetHeight - pt[1]];

  parts.push(header(sheetWidthMM, laid.sheetHeight));
  parts.push(tables());
  parts.push(blocks());
  parts.push(pair(0, 'SECTION') + pair(2, 'ENTITIES'));

  for (const p of laid.panels) {
    parts.push(polyline(p.expanded.map(flipY), 'CUT', true));
    // Dashed stitch line through the centre of the holes — layer SEAM.
    parts.push(polyline(p.original.map(flipY), 'SEAM', true));
    if (p.foam) {
      parts.push(polyline(p.foam.map(flipY), 'FOAM', true));
    }
    // Punch-through apertures: each aperture emits its own CUT + SEAM
    // polyline (plus FOAM if enabled) so the DXF has a matching hole.
    for (const a of p.apertures || []) {
      parts.push(polyline(a.cut.map(flipY), 'CUT', true));
      parts.push(polyline(a.stitch.map(flipY), 'SEAM', true));
      if (a.foam) {
        parts.push(polyline(a.foam.map(flipY), 'FOAM', true));
      }
    }
    for (const h of p.holes) {
      if (holeStyle === 'slit') {
        // Flip Y here too: DXF Y-up means the local seam tangent angle
        // flips sign. 45° offset is relative to that flipped tangent.
        const theta = -(h.angle || 0) + Math.PI / 4;
        const dx = Math.cos(theta) * slitHalf;
        const dy = Math.sin(theta) * slitHalf;
        const [hx, hy] = flipY([h.x, h.y]);
        parts.push(polyline([[hx - dx, hy - dy], [hx + dx, hy + dy]], 'HOLES', false));
      } else {
        const [hx, hy] = flipY([h.x, h.y]);
        parts.push(circle(hx, hy, holeRadius, 'HOLES'));
      }
    }
    for (const el of p.edgeLabels || []) {
      const [lx, ly] = flipY([el.x, el.y]);
      parts.push(text(lx, ly - 0.7, 1.4, `P${el.neighbor + 1}`, 'LABEL'));
    }
    // Panel number + copy count in the centre
    const [cx, cy] = flipY(p.labelPos);
    const label = p.copyCount > 1 ? `P${p.idx + 1} x${p.copyCount}` : `P${p.idx + 1}`;
    parts.push(text(cx, cy, 4, label, 'LABEL'));
  }

  // Legend at the top of the sheet
  const legendY = laid.sheetHeight - (marginMM - 1);
  const legend =
    `Panels Studio | ${laid.panels.length} panels | ${ballDiameterMM}mm ball | ` +
    `+${seamAllowanceMM}mm seam | ${holeStyle === 'slit' ? `${slitLengthMM}mm slits @ 45°` : `${holeDiameterMM}mm holes`} @ ${holeSpacingMM}mm` +
    (foamInsetMM > 0 ? ` | foam ${foamInsetMM}mm inset` : '') +
    (spherized ? ' | spherized' : '');
  parts.push(text(marginMM + 60, legendY, 3, legend, 'TITLE'));

  parts.push(pair(0, 'ENDSEC'));
  parts.push(pair(0, 'EOF'));
  return parts.join('');
}
